import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, Plus, Search } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useBookingRealtimeRefresh } from "@/lib/useBookingRealtimeRefresh";
import { usePageHeader } from "@/layout/usePageHeader";
import {
  addDaysLocal,
  countNightsLocal,
  formatIsoDateShort,
  todayLocal,
} from "@/lib/bookingDates";
import { useCurrentUser } from "@/lib/auth";
import { canWriteGuestPitchBooking } from "@/lib/permissions";
import {
  buildPitchPlannerRows,
  type PitchTimelineSegment,
  type PitchTimelineSegmentKind,
} from "@/lib/pitchPlanner";
import {
  GANTT_DAY_WIDTH_PX,
  GANTT_INITIAL_WEEKS,
  GANTT_LABEL_WIDTH_PX,
  GANTT_LOAD_MORE_WEEKS,
  GANTT_WEEK_DAYS,
} from "@/lib/pitchPlannerGantt";
import { categoryColorOrDefault } from "@/lib/pitchCategoryColors";
import { getGermanPublicHoliday } from "@/lib/germanHolidays";
import {
  isPlannerEventsLabel,
  loadRegionCalendar,
  PLANNER_EVENTS_LABEL,
  type RegionCalendar,
  type RegionHoliday,
} from "@/lib/stayCalendarNotes";
import { isPitchFixed } from "@/lib/pitchAssignment";
import { resolveGuestName } from "@/lib/guestDisplay";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Button } from "@/components/ui/Button";
import {
  PitchPlannerGanttChart,
  type PitchPlannerGanttHandle,
} from "@/components/planner/PitchPlannerGanttChart";
import { PlannerSelectionPanel } from "@/components/planner/PlannerSelectionPanel";
import { usePlannerFullscreen } from "@/pages/planner/usePlannerFullscreen";
import { PlannerCustomEntryModal } from "@/components/planner/PlannerCustomEntryModal";
import { PlannerCustomRowModal } from "@/components/planner/PlannerCustomRowModal";
import { EditBookingModal } from "@/pages/bookings/EditBookingModal";
import { GuestStayModal } from "@/components/bookings/GuestStayModal";
import { useGuestById } from "@/lib/useGuestById";
import type { Booking, Pitch, PlannerCustomEntry, PlannerCustomRow } from "@/types/camping";

const DEFAULT_RANGE_DAYS = 30;
const GANTT_INITIAL_DAYS = GANTT_INITIAL_WEEKS * GANTT_WEEK_DAYS;
const GANTT_LOAD_MORE_DAYS = GANTT_LOAD_MORE_WEEKS * GANTT_WEEK_DAYS;
const GANTT_LEADING_DAYS = 14;

/** Genug Tage links und rechts, damit „heute“ mittig sitzt und die Kante nicht sofort nachlädt. */
function initialGanttSpan(today: string): { from: string; to: string } {
  let lead = GANTT_LEADING_DAYS;
  let ahead = GANTT_INITIAL_DAYS;
  if (typeof window !== "undefined") {
    const trackPx = Math.max(GANTT_DAY_WIDTH_PX * 28, window.innerWidth - GANTT_LABEL_WIDTH_PX);
    const half = Math.ceil(trackPx / (GANTT_DAY_WIDTH_PX * 2)) + GANTT_WEEK_DAYS;
    lead = Math.min(90, Math.max(lead, half));
    ahead = Math.min(120, Math.max(ahead, half));
  }
  return { from: addDaysLocal(today, -lead), to: addDaysLocal(today, ahead - 1) };
}
type PlannerViewMode = "list" | "gantt";

function validatePlannerRange(from: string, toInclusive: string): string | null {
  if (!from || !toInclusive) return "Bitte einen Zeitraum wählen.";
  if (toInclusive < from) return "Das Enddatum muss am oder nach dem Startdatum liegen.";
  return null;
}

function segmentKindLabel(kind: PitchTimelineSegmentKind): string {
  switch (kind) {
    case "free":
      return "Frei";
    case "occupied":
      return "Belegt";
    case "blocked_pitch":
      return "Platz gesperrt";
  }
}

function segmentRangeLabel(from: string, to: string): string {
  const nights = countNightsLocal(from, to);
  const range = `${formatIsoDateShort(from)} – ${formatIsoDateShort(to)}`;
  if (nights <= 0) return range;
  return `${range} (${nights} ${nights === 1 ? "Nacht" : "Nächte"})`;
}

function SegmentRow({
  segment,
  categoryColor,
  guestName,
  selected,
  onOpenBooking,
  onEditBooking,
}: {
  segment: PitchTimelineSegment;
  categoryColor: string | null | undefined;
  guestName: (guestId: string, booking?: Booking | null) => string;
  selected?: boolean;
  onOpenBooking: (booking: Booking) => void;
  onEditBooking?: (booking: Booking) => void;
}) {
  const label = segmentKindLabel(segment.kind);
  const range = segmentRangeLabel(segment.from, segment.to);
  const booking = segment.booking;
  const occupiedColor = categoryColorOrDefault(categoryColor);
  const isFixed = booking ? isPitchFixed(booking.pitch_assignment_mode) : false;

  const interactive = Boolean(booking);
  const rowClass = clsx(
    "planner-segment",
    segment.kind === "free" && "is-free",
    segment.kind === "occupied" && "is-occupied",
    segment.kind === "blocked_pitch" && "is-blocked",
    interactive && "is-interactive",
    selected && "is-selected",
  );

  const content = (
    <>
      <span className="planner-segment__kind">
        {isFixed ? "! " : ""}
        {label}
      </span>
      <span className="planner-segment__range">{range}</span>
      {booking ? (
        <span className="planner-segment__meta">
          <span>{guestName(booking.guest_id, booking)}</span>
          <span className="planner-segment__booking">{booking.booking_number}</span>
          <StatusBadge status={booking.status} />
        </span>
      ) : segment.kind === "blocked_pitch" ? (
        <span className="planner-segment__meta">Stellplatz ist inaktiv oder manuell gesperrt.</span>
      ) : null}
    </>
  );

  const occupiedStyle =
    segment.kind === "occupied"
      ? ({ "--segment-color": occupiedColor } as CSSProperties)
      : undefined;

  if (!interactive || !booking) {
    return (
      <li className={rowClass} style={occupiedStyle}>
        {content}
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        className={rowClass}
        style={occupiedStyle}
        aria-pressed={selected}
        onClick={() => onOpenBooking(booking)}
        onDoubleClick={() => onEditBooking?.(booking)}
      >
        {content}
      </button>
    </li>
  );
}

export function PitchPlannerPage() {
  const { setPageMeta } = usePageHeader();
  const { fullscreen, toggle: toggleFullscreen } = usePlannerFullscreen();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteGuestPitchBooking(groups);
  const today = todayLocal();
  const defaultListTo = addDaysLocal(today, DEFAULT_RANGE_DAYS);

  const [listRangeFrom, setListRangeFrom] = useState(today);
  const [listRangeTo, setListRangeTo] = useState(defaultListTo);
  const [ganttRangeFrom, setGanttRangeFrom] = useState(() => initialGanttSpan(today).from);
  const [ganttRangeTo, setGanttRangeTo] = useState(() => initialGanttSpan(today).to);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [codeFilter, setCodeFilter] = useState("");

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [customEntries, setCustomEntries] = useState<PlannerCustomEntry[]>([]);
  const [customRows, setCustomRows] = useState<PlannerCustomRow[]>([]);
  const [regionCalendar, setRegionCalendar] = useState<RegionCalendar | null>(null);
  const [viewMode, setViewMode] = useState<PlannerViewMode>("gantt");
  const [customRowModalOpen, setCustomRowModalOpen] = useState(false);
  const [customEntryModalOpen, setCustomEntryModalOpen] = useState(false);
  const [editingCustomEntry, setEditingCustomEntry] = useState<PlannerCustomEntry | null>(null);
  const [entryPresetLabel, setEntryPresetLabel] = useState<string | undefined>();
  const [entryPresetFrom, setEntryPresetFrom] = useState<string | undefined>();
  const [entryPresetTo, setEntryPresetTo] = useState<string | undefined>();
  const [entryLockLabel, setEntryLockLabel] = useState(false);
  const [entryEndInclusive, setEntryEndInclusive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [booted, setBooted] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stayModalBooking, setStayModalBooking] = useState<Booking | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [inspectedBooking, setInspectedBooking] = useState<Booking | null>(null);
  const [inspectedCustom, setInspectedCustom] = useState<PlannerCustomEntry | null>(null);
  const ganttRef = useRef<PitchPlannerGanttHandle>(null);
  const staticReadyRef = useRef(false);
  const rangeRequestRef = useRef(0);
  const refreshRequestRef = useRef(0);
  const bootedRef = useRef(false);

  const activeRangeFrom = viewMode === "gantt" ? ganttRangeFrom : listRangeFrom;
  const activeRangeTo = viewMode === "gantt" ? ganttRangeTo : listRangeTo;
  const rangeToExclusive = addDaysLocal(activeRangeTo, 1);
  const rangeError = viewMode === "list" ? validatePlannerRange(listRangeFrom, listRangeTo) : null;
  const prevGanttRangeKeyRef = useRef<string | null>(null);

  const loadRange = useCallback(
    (from: string, toInclusive: string, options?: { more?: boolean; silent?: boolean }) => {
      const silent = options?.silent === true && bootedRef.current;
      const isMore = options?.more ?? false;
      if (viewMode === "list" && validatePlannerRange(listRangeFrom, listRangeTo)) {
        setLoading(false);
        return;
      }
      const rangeId = silent ? rangeRequestRef.current : ++rangeRequestRef.current;
      const refreshId = ++refreshRequestRef.current;
      if (!silent) {
        if (isMore) setLoadingMore(true);
        else setLoading(true);
        setError(null);
      }
      const toExclusive = addDaysLocal(toInclusive, 1);
      const params = new URLSearchParams({
        from,
        to: toExclusive,
      });
      const needsStatic = !staticReadyRef.current;
      const isStale = () => {
        if (!silent && rangeId !== rangeRequestRef.current) return true;
        if (silent && (refreshId !== refreshRequestRef.current || rangeId !== rangeRequestRef.current)) return true;
        return false;
      };
      const apply = (
        entries: PlannerCustomEntry[],
        bookings: Booking[],
        pitches: Pitch[] | null,
        rows: PlannerCustomRow[] | null,
        calendar: RegionCalendar | null | undefined,
      ) => {
        if (isStale()) return;
        setBookings(bookings);
        setCustomEntries(entries);
        if (pitches) {
          setPitches(pitches);
          staticReadyRef.current = true;
        }
        if (rows) setCustomRows(rows);
        if (calendar !== undefined) setRegionCalendar(calendar);
      };
      Promise.all([
        api.get<Booking[]>(`/api/v1/site-plan/pitch-bookings?${params}`),
        needsStatic && !silent ? api.get<Pitch[]>("/api/v1/pitches") : Promise.resolve(null),
        api
          .get<PlannerCustomEntry[]>(`/api/v1/planner/custom-entries?from=${from}&to=${toExclusive}`)
          .catch(() => [] as PlannerCustomEntry[]),
        needsStatic && !silent
          ? api.get<PlannerCustomRow[]>("/api/v1/planner/custom-rows").catch(() => [] as PlannerCustomRow[])
          : Promise.resolve(null),
        silent ? Promise.resolve(undefined) : loadRegionCalendar(from, toExclusive),
      ])
        .then(([bookings, pitches, entries, rows, calendar]) => {
          apply(entries, bookings, pitches, rows, calendar);
        })
        .catch((e: Error) => {
          if (isStale()) return;
          setError(e.message);
        })
        .finally(() => {
          if (isStale()) return;
          if (!silent) {
            setLoading(false);
            setLoadingMore(false);
          }
          bootedRef.current = true;
          setBooted(true);
        });
    },
    [listRangeFrom, listRangeTo, viewMode],
  );

  useEffect(() => {
    prevGanttRangeKeyRef.current = null;
  }, [viewMode]);

  useEffect(() => {
    const from = viewMode === "gantt" ? ganttRangeFrom : listRangeFrom;
    const to = viewMode === "gantt" ? ganttRangeTo : listRangeTo;
    const ganttRangeKey = `${ganttRangeFrom}|${ganttRangeTo}`;
    const loadingMoreGantt =
      viewMode === "gantt" &&
      prevGanttRangeKeyRef.current !== null &&
      prevGanttRangeKeyRef.current !== ganttRangeKey;
    if (viewMode === "gantt") {
      prevGanttRangeKeyRef.current = ganttRangeKey;
    }
    loadRange(from, to, { more: loadingMoreGantt });
  }, [
    ganttRangeFrom,
    ganttRangeTo,
    listRangeFrom,
    listRangeTo,
    loadRange,
    viewMode,
  ]);
  useEffect(() => {
    setPageMeta({
      title: "Planer",
      subtitle: "Belegung und freie Zeiträume je Stellplatz",
    });
    return () => setPageMeta(null);
  }, [setPageMeta]);

  const reload = useCallback(() => {
    const from = viewMode === "gantt" ? ganttRangeFrom : listRangeFrom;
    const to = viewMode === "gantt" ? ganttRangeTo : listRangeTo;
    loadRange(from, to, { silent: true });
  }, [ganttRangeFrom, ganttRangeTo, listRangeFrom, listRangeTo, loadRange, viewMode]);

  useBookingRealtimeRefresh(reload);

  const categories = useMemo(() => {
    const names = new Set<string>();
    for (const p of pitches) {
      if (p.category_name) names.add(p.category_name);
    }
    return [...names].sort((a, b) => a.localeCompare(b, "de"));
  }, [pitches]);

  const filteredPitches = useMemo(() => {
    const code = codeFilter.trim().toLowerCase();
    return pitches.filter((p) => {
      if (categoryFilter && p.category_name !== categoryFilter) return false;
      if (code && !p.code.toLowerCase().includes(code)) return false;
      return true;
    });
  }, [pitches, categoryFilter, codeFilter]);

  const visibleHolidays = useMemo(() => {
    if (regionCalendar) return regionCalendar.holidays;
    if (!booted) return null;
    const items: RegionHoliday[] = [];
    let current = activeRangeFrom;
    while (current <= activeRangeTo) {
      const name = getGermanPublicHoliday(current);
      if (name) items.push({ date: current, name });
      current = addDaysLocal(current, 1);
    }
    return items;
  }, [activeRangeFrom, activeRangeTo, booted, regionCalendar]);
  const holidayPlaceLabel = regionCalendar?.place_label ?? (booted ? "bundesweit" : null);

  const plannerRows = useMemo(() => {
    if (rangeError) return [];
    return buildPitchPlannerRows(filteredPitches, bookings, activeRangeFrom, rangeToExclusive, today);
  }, [filteredPitches, bookings, activeRangeFrom, rangeToExclusive, rangeError, today]);

  const guestName = useCallback(
    (id: string, booking?: Booking | null) => resolveGuestName(id, { booking }),
    [],
  );

  const stayModalGuest = useGuestById(stayModalBooking?.guest_id);

  const stayModalPitch = useMemo(
    () =>
      stayModalBooking?.pitch_id
        ? pitches.find((p) => p.id === stayModalBooking.pitch_id) ?? null
        : null,
    [stayModalBooking, pitches],
  );

  const selectedBookingGuest = useGuestById(selectedBooking?.guest_id);

  const inspectedPitch = useMemo(() => {
    const pitchId = inspectedBooking?.pitch_id;
    if (!pitchId) return null;
    return pitches.find((pitch) => pitch.id === pitchId) ?? null;
  }, [inspectedBooking, pitches]);

  useEffect(() => {
    setInspectedBooking((current) => {
      if (!current) return current;
      return bookings.find((booking) => booking.id === current.id) ?? null;
    });
  }, [bookings]);

  useEffect(() => {
    setInspectedCustom((current) => {
      if (!current) return current;
      return customEntries.find((entry) => entry.id === current.id) ?? null;
    });
  }, [customEntries]);

  useEffect(() => {
    if (!inspectedBooking && !inspectedCustom) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (document.querySelector("[role='dialog']")) return;
      event.stopImmediatePropagation();
      setInspectedBooking(null);
      setInspectedCustom(null);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [inspectedBooking, inspectedCustom]);

  const rangeLabel =
    viewMode === "gantt"
      ? `${formatIsoDateShort(ganttRangeFrom)} – ${formatIsoDateShort(ganttRangeTo)}`
      : `${formatIsoDateShort(listRangeFrom)} – ${formatIsoDateShort(listRangeTo)}`;

  const inspectBooking = useCallback((booking: Booking) => {
    setInspectedCustom(null);
    setInspectedBooking(booking);
  }, []);

  const inspectCustom = useCallback((entry: PlannerCustomEntry) => {
    setInspectedBooking(null);
    setInspectedCustom(entry);
  }, []);

  const clearInspection = useCallback(() => {
    setInspectedBooking(null);
    setInspectedCustom(null);
  }, []);

  const editStay = useCallback((booking: Booking) => {
    setInspectedCustom(null);
    setInspectedBooking(booking);
    setSelectedBooking(booking);
  }, []);

  const openCreateCustomEntry = useCallback((rowLabel: string, startDate: string) => {
    const events = isPlannerEventsLabel(rowLabel);
    setEditingCustomEntry(null);
    setEntryPresetLabel(rowLabel);
    setEntryPresetFrom(startDate);
    setEntryPresetTo(addDaysLocal(startDate, events ? 0 : 7));
    setEntryLockLabel(true);
    setEntryEndInclusive(events);
    setCustomEntryModalOpen(true);
  }, []);

  const openEditCustomEntry = useCallback((entry: PlannerCustomEntry) => {
    setEditingCustomEntry(entry);
    setEntryPresetLabel(undefined);
    setEntryPresetFrom(undefined);
    setEntryPresetTo(undefined);
    setEntryLockLabel(isPlannerEventsLabel(entry.label));
    setEntryEndInclusive(isPlannerEventsLabel(entry.label));
    setCustomEntryModalOpen(true);
  }, []);

  const handleLoadPast = useCallback(() => {
    setGanttRangeFrom((current) => addDaysLocal(current, -GANTT_LOAD_MORE_DAYS));
  }, []);

  const handleLoadFuture = useCallback(() => {
    setGanttRangeTo((current) => addDaysLocal(current, GANTT_LOAD_MORE_DAYS));
  }, []);

  return (
    <div className={clsx("planner-page", fullscreen && "planner-fullscreen-root")}>
      <GuestStayModal
        open={!!stayModalBooking}
        booking={stayModalBooking}
        guest={stayModalGuest}
        pitch={stayModalPitch}
        canEdit={canEdit}
        userGroups={groups}
        onInvoiceChanged={reload}
        onClose={() => setStayModalBooking(null)}
        onEditBooking={() => {
          if (stayModalBooking) {
            setSelectedBooking(stayModalBooking);
            setStayModalBooking(null);
          }
        }}
      />

      <EditBookingModal
        open={!!selectedBooking}
        booking={selectedBooking}
        guest={selectedBookingGuest}
        pitches={pitches}
        onClose={() => setSelectedBooking(null)}
        onSaved={() => {
          setSelectedBooking(null);
          reload();
        }}
        canEdit={canEdit}
      />

      <div className="planner-toolbar">
        <div className="planner-toolbar__group">
          {fullscreen ? <h2 className="planner-title">Planer</h2> : null}
          <div className="eh-segmented" role="tablist" aria-label="Ansicht">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "gantt"}
              className={clsx("eh-segmented-item", viewMode === "gantt" && "is-active")}
              onClick={() => setViewMode("gantt")}
            >
              Balken
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "list"}
              className={clsx("eh-segmented-item", viewMode === "list" && "is-active")}
              onClick={() => setViewMode("list")}
            >
              Liste
            </button>
          </div>
          {viewMode === "gantt" ? (
            <div className="planner-nav">
              <button
                type="button"
                className="planner-nav__btn"
                aria-label="Eine Woche früher"
                onClick={() => ganttRef.current?.scrollByDays(-7)}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                className="planner-nav__today"
                onClick={() => ganttRef.current?.scrollToToday()}
              >
                Heute
              </button>
              <button
                type="button"
                className="planner-nav__btn"
                aria-label="Eine Woche später"
                onClick={() => ganttRef.current?.scrollByDays(7)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          ) : (
            <>
              <input
                type="date"
                className="planner-field"
                aria-label="Von"
                value={listRangeFrom}
                onChange={(e) => setListRangeFrom(e.target.value)}
              />
              <input
                type="date"
                className="planner-field"
                aria-label="Bis einschließlich"
                value={listRangeTo}
                min={listRangeFrom}
                onChange={(e) => setListRangeTo(e.target.value)}
              />
            </>
          )}
          <span className="planner-range">{rangeError ? "Ungültiger Zeitraum" : rangeLabel}</span>
          {booted ? <span className="planner-count">{filteredPitches.length} Plätze</span> : null}
        </div>
        <div className="planner-toolbar__group planner-toolbar__group--filters">
          <label className="planner-search">
            <Search size={14} aria-hidden />
            <input
              type="search"
              className="planner-field"
              aria-label="Platzcode"
              placeholder="Platz suchen"
              value={codeFilter}
              onChange={(e) => setCodeFilter(e.target.value)}
            />
          </label>
          <select
            className="planner-field planner-field--select"
            aria-label="Kategorie"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">Alle Kategorien</option>
            {categories.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          {canEdit && viewMode === "gantt" ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leftIcon={<Plus size={15} aria-hidden />}
              onClick={() => setCustomRowModalOpen(true)}
            >
              Zeile hinzufügen
            </Button>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-pressed={fullscreen}
            title={fullscreen ? "Vollbild beenden (Esc)" : "Planer im Vollbild anzeigen"}
            leftIcon={
              fullscreen ? (
                <Minimize2 size={15} strokeWidth={2} aria-hidden />
              ) : (
                <Maximize2 size={15} strokeWidth={2} aria-hidden />
              )
            }
            onClick={toggleFullscreen}
          >
            {fullscreen ? "Vollbild beenden" : "Vollbild"}
          </Button>
        </div>
      </div>
      <p className="planner-note">
        Abreisetag zählt als frei. Klick öffnet Details, Doppelklick bearbeitet.{" "}
        <Link to="/platzplan">Platzplan</Link>
      </p>
      {rangeError ? <p className="planner-banner planner-banner--error">{rangeError}</p> : null}

      {error ? <p className="planner-banner planner-banner--error">{error}</p> : null}
      {!booted && loading ? (
        <div className="pp-shell pp-skeleton" role="status" aria-label="Lade Plätze und Buchungen">
          {Array.from({ length: 9 }, (_, index) => (
            <div key={index} className="pp-skeleton__row" />
          ))}
        </div>
      ) : viewMode === "list" && rangeError ? null : (
        <div className="flex min-h-0 min-w-0 flex-1 gap-3">
          {viewMode === "gantt" ? (
            <PitchPlannerGanttChart
              ref={ganttRef}
              rows={plannerRows}
              customEntries={customEntries}
              customRows={customRows}
              rangeFrom={ganttRangeFrom}
              rangeTo={ganttRangeTo}
              loadingMore={loadingMore}
              refreshing={booted && loading && !loadingMore}
              fillHeight
              selectedBookingId={inspectedBooking?.id ?? null}
              selectedCustomEntryId={inspectedCustom?.id ?? null}
              onOpenBooking={inspectBooking}
              onEditBooking={canEdit ? editStay : undefined}
              onOpenCustomEntry={inspectCustom}
              onEditCustomEntry={canEdit ? openEditCustomEntry : undefined}
              onAddCustomEntry={canEdit ? openCreateCustomEntry : undefined}
              onClearSelection={clearInspection}
              holidays={visibleHolidays}
              holidayPlaceLabel={holidayPlaceLabel}
              onLoadPast={handleLoadPast}
              onLoadFuture={handleLoadFuture}
            />
          ) : (
        <ul className="planner-list">
          <li>
            <section className="planner-pitch">
              <header className="planner-pitch__head">
                <span className="planner-pitch__rail" style={{ backgroundColor: "#6366f1" }} aria-hidden />
                <div className="min-w-0 flex-1">
                  <h2 className="planner-pitch__code">{PLANNER_EVENTS_LABEL}</h2>
                  <p className="planner-pitch__meta">Eigene Termine des Platzes</p>
                </div>
                {canEdit ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => openCreateCustomEntry(PLANNER_EVENTS_LABEL, listRangeFrom)}
                  >
                    Hinzufügen
                  </Button>
                ) : null}
              </header>
              <ul className="planner-pitch__segments">
                {customEntries.filter((entry) => isPlannerEventsLabel(entry.label)).length === 0 ? (
                  <li className="text-sm text-label-secondary">Noch keine Veranstaltung.</li>
                ) : (
                  customEntries
                    .filter((entry) => isPlannerEventsLabel(entry.label))
                    .map((entry) => (
                      <li key={entry.id}>
                        <button
                          type="button"
                          className="planner-segment is-interactive"
                          onClick={() => (canEdit ? openEditCustomEntry(entry) : inspectCustom(entry))}
                        >
                          <span className="planner-segment__kind">Termin</span>
                          <span className="planner-segment__range">
                            {(() => {
                              const lastDay = addDaysLocal(entry.end_date, -1);
                              return lastDay === entry.start_date
                                ? formatIsoDateShort(entry.start_date)
                                : `${formatIsoDateShort(entry.start_date)} – ${formatIsoDateShort(lastDay)}`;
                            })()}
                          </span>
                          <span className="planner-segment__meta">{entry.title}</span>
                        </button>
                      </li>
                    ))
                )}
              </ul>
            </section>
          </li>
          <li>
            <section className="planner-pitch">
              <header className="planner-pitch__head">
                <span className="planner-pitch__rail" style={{ backgroundColor: "#f59e0b" }} aria-hidden />
                <div className="min-w-0">
                  <h2 className="planner-pitch__code">Feiertage</h2>
                  <p className="planner-pitch__meta">{holidayPlaceLabel ?? "wird ermittelt"}</p>
                </div>
              </header>
              <ul className="planner-pitch__segments">
                {(visibleHolidays?.length ?? 0) === 0 ? (
                  <li className="text-sm text-label-secondary">
                    {visibleHolidays ? "Keine Feiertage in diesem Zeitraum." : "Feiertage werden ermittelt."}
                  </li>
                ) : (
                  visibleHolidays?.map((holiday) => (
                    <li key={holiday.date} className="planner-segment">
                      <span className="planner-segment__kind">Feiertag</span>
                      <span className="planner-segment__range">{formatIsoDateShort(holiday.date)}</span>
                      <span className="planner-segment__meta">{holiday.name}</span>
                    </li>
                  ))
                )}
              </ul>
            </section>
          </li>
          {plannerRows.length === 0 ? (
            <li className="planner-empty">Keine Stellplätze für die Filterauswahl.</li>
          ) : null}
          {plannerRows.map(({ pitch, segments }) => (
            <li key={pitch.id}>
              <section className="planner-pitch">
                <header className="planner-pitch__head">
                  <span
                    className="planner-pitch__rail"
                    style={{ backgroundColor: categoryColorOrDefault(pitch.category_color) }}
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <h2 className="planner-pitch__code">
                      {pitch.code}
                      {pitch.label ? <span className="planner-pitch__label">{pitch.label}</span> : null}
                    </h2>
                    <div className="planner-pitch__meta">
                      <span>{pitch.category_name ?? "Ohne Kategorie"}</span>
                      {pitch.is_blocked ? <span className="planner-chip planner-chip--blocked">Gesperrt</span> : null}
                      {!pitch.is_active ? <span className="planner-chip planner-chip--inactive">Inaktiv</span> : null}
                    </div>
                  </div>
                </header>
                <ul className="planner-pitch__segments">
                  {segments.length === 0 ? (
                    <li className="text-sm text-label-secondary">Keine Segmente im Zeitraum.</li>
                  ) : (
                    segments.map((segment, idx) => (
                      <SegmentRow
                        key={`${segment.kind}-${segment.from}-${segment.to}-${idx}`}
                        segment={segment}
                        categoryColor={pitch.category_color}
                        guestName={guestName}
                        selected={segment.booking?.id === inspectedBooking?.id}
                        onOpenBooking={inspectBooking}
                        onEditBooking={canEdit ? editStay : undefined}
                      />
                    ))
                  )}
                </ul>
              </section>
            </li>
          ))}
        </ul>
          )}
          {inspectedBooking || inspectedCustom ? (
            <PlannerSelectionPanel
              booking={inspectedBooking}
              pitch={inspectedPitch}
              customEntry={inspectedCustom}
              canEdit={canEdit}
              onClose={clearInspection}
              onEditStay={() => {
                if (inspectedBooking) editStay(inspectedBooking);
              }}
              onShowStayDetails={() => {
                if (inspectedBooking) setStayModalBooking(inspectedBooking);
              }}
              onEditCustomEntry={() => {
                if (inspectedCustom) openEditCustomEntry(inspectedCustom);
              }}
            />
          ) : null}
        </div>
      )}

      <PlannerCustomRowModal
        open={customRowModalOpen}
        onClose={() => setCustomRowModalOpen(false)}
        onSaved={reload}
      />

      <PlannerCustomEntryModal
        open={customEntryModalOpen}
        entry={editingCustomEntry}
        defaultRangeFrom={entryPresetFrom ?? activeRangeFrom}
        defaultRangeTo={entryPresetTo ?? rangeToExclusive}
        presetLabel={entryPresetLabel}
        lockLabel={entryLockLabel}
        endInclusive={entryEndInclusive}
        onClose={() => {
          setCustomEntryModalOpen(false);
          setEditingCustomEntry(null);
          setEntryPresetLabel(undefined);
          setEntryPresetFrom(undefined);
          setEntryPresetTo(undefined);
          setEntryLockLabel(false);
          setEntryEndInclusive(false);
        }}
        onSaved={reload}
      />
    </div>
  );
}
